import { formUtils as leavesFormUtils } from '@everlywell/leaves';

const ALLOWED_DIRECTION_KEYS = [
  'Backspace',
  'ArrowLeft',
  'ArrowRight',
  'Delete',
  'Tab',
];

const ALLOWED_NUMERIC_KEYS = [...ALLOWED_DIRECTION_KEYS, '.'];

/**
 * returns a boolean value
 * @param email a string to check against regex
 */
export const isValidEmail = (email: string) =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );

/**
 * Checks if a given ZIP code is located in New York.
 *
 * @param {string} value - The ZIP code to check.
 * @returns {boolean} Returns `true` if the ZIP code is not in New York, otherwise `false`.
 */
export const isZipCodeInNewYork = (zipCode: number): boolean => {
  const isNY =
    (zipCode >= 10000 && zipCode <= 14999) ||
    zipCode === 6390 ||
    zipCode === 501 ||
    zipCode === 544;

  return isNY;
};

/**
 * Checks if a given ZIP code is valid.
 * @param zipCode
 * @returns
 */
export const isValidZipCode = (zipCode: string) =>
  leavesFormUtils.validateZipCode(zipCode);

/**
 * Checks if a given phone number is valid.
 * @param phoneNumber
 * @returns
 */
export const isValidPhoneNumber = (phoneNumber: string) =>
  leavesFormUtils.validatePhoneNumber(phoneNumber);

const numbersOnly = (str: string): RegExpMatchArray | null => {
  const onlyNumbers = /[0-9]/;
  return str.match(onlyNumbers);
};

/**
 * Formats the phone number string as (123) 456-7890
 * @param value input value for phone number
 * @param options options for phone number
 * @param options.format format for phone number @default parentheses
 * @returns
 */
export const formatPhoneNumber = (
  value: string = '',
  options: {
    format?: 'parentheses' | 'hyphens';
  } = {},
): string => {
  const input = value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
  const areaCode = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);

  const format = options.format || 'parentheses';

  if (format === 'parentheses') {
    if (numbersOnly(input) && input.length > 6) {
      return `(${areaCode}) ${middle}-${last}`;
    } else if (numbersOnly(input) && input.length > 3) {
      return `(${areaCode}) ${middle}`;
    } else if (numbersOnly(input) && input.length > 0) {
      return `(${areaCode}`;
    }
  } else if (format === 'hyphens') {
    if (numbersOnly(input) && input.length > 6) {
      return `${areaCode}-${middle}-${last}`;
    } else if (numbersOnly(input) && input.length > 3) {
      return `${areaCode}-${middle}`;
    } else if (numbersOnly(input) && input.length > 0) {
      return `${areaCode}`;
    }
  }

  return '';
};

/**
 * Checks if a given ZIP code is located in Arizona, Alaska, Hawaii, or New York.
 *
 * @param {string} value - The ZIP code to check.
 * @returns {boolean} Returns `true` if the ZIP code is in one of the states above, otherwise `false`.
 */
export const isZipCodeInIneligibleQuestState = (zipCode: number): boolean => {
  const isAZ = zipCode >= 85001 && zipCode <= 86556;
  const isAK = zipCode >= 99501 && zipCode <= 99950;
  const isHI = zipCode >= 96701 && zipCode <= 96898;
  const isRI = zipCode >= 2801 && zipCode <= 2940;
  const isNJ = zipCode >= 7001 && zipCode <= 8989;

  return isAZ || isAK || isHI || isRI || isNJ || isZipCodeInNewYork(zipCode);
};

/**
 * Function to restrict input in number fields to only numeric values.
 * Allows navigation keys (Backspace, ArrowLeft, ArrowRight, Delete, Tab) and prevents invalid characters.
 *
 * @param event - The keyboard event triggered by user input
 * @param allowZeroAtStart - Optional parameter to allow or disallow zero at the start of the input
 * @returns void
 */
export const restrictNumericInputEvent = (
  event: React.KeyboardEvent<HTMLInputElement>,
  allowZeroAtStart = false,
) => {
  const { key, currentTarget, target } = event;

  if (ALLOWED_NUMERIC_KEYS.includes(key)) {
    // Prevent multiple decimal points
    if (key === '.' && (target as HTMLInputElement).value.includes('.')) {
      event.preventDefault();
    }
    return;
  }

  // Allow only numbers
  if (!/^\d$/.test(key)) {
    event.preventDefault();
    return;
  }

  // Not allow 0 when the cursor is at the beginning
  if (!allowZeroAtStart && key === '0' && currentTarget.selectionStart === 0) {
    event.preventDefault();
  }
};

export const restrictCharacterLength = (
  event: React.KeyboardEvent<HTMLInputElement>,
  limit: number,
) => {
  const input = event.target as HTMLInputElement;
  if (
    input.value.length >= limit &&
    !ALLOWED_DIRECTION_KEYS.includes(event.key)
  ) {
    event.preventDefault();
  }
};
